import { atom } from 'jotai';
import { Property, clientToProperty } from '@/types/web/property';
import { Client } from '@/types/api/clients';
import { atomFamily } from 'jotai/utils';
import { TokenService } from '@/services/auth/tokenService';
import { createApiClient } from '@/services/api/httpClient';

// Use empty search to get all clients (returns Client objects, not Properties)
export const allClientsAtom = atom(async (get) => {
  const clients = await get(filteredClientsFamily(''));
  return clients;
});

// Atom family to get individual clients by ID (returns full Client object)
export const clientAtomFamily = atomFamily((clientId: string) =>
  atom(async (get) => {
    const allClients = await get(allClientsAtom);
    const client = allClients.find((c) => c.client_id === clientId);
    return client || null;
  }),
);

// Atom for selected property IDs (instead of full entities)
export const selectedPropertyIdsAtom = atom<string[]>([]);

// Derived atom that converts IDs to full Property objects (converts Client to Property for UI)
export const selectedPropertiesAtom = atom(
  async (get) => {
    const selectedIds = get(selectedPropertyIdsAtom);
    const allClients = await get(allClientsAtom);

    return selectedIds
      .map((id) => allClients.find((c) => c.client_id === id))
      .filter((client): client is Client => Boolean(client))
      .map(clientToProperty);
  },
  (_, set, selectedProperties: Property[]) => {
    const ids = selectedProperties.map((prop) => prop.id);
    set(selectedPropertyIdsAtom, ids);
  },
);

// Atom for managing dialog open state
export const propertySearchOpenAtom = atom<boolean>(false);

export const filteredClientsFamily = atomFamily((searchTerm: string) =>
  atom(async () => {
    const tokenService = TokenService.getInstance();
    const authToken = await tokenService.getCurrentToken();

    if (!authToken) {
      throw new Error('Authentication required');
    }

    const apiClient = createApiClient(() => authToken);

    if (!searchTerm.trim()) {
      // Use single search endpoint to get all clients
      const response = await apiClient.get<{ clients: Client[] }>(
        '/clients?action=search&client_name=',
      );
      return response.clients;
    }

    // Execute parallel searches across different fields
    const [nameResults, addressResults, stateResults] = await Promise.all([
      apiClient.get<{ clients: Client[] }>(
        `/clients?action=search&client_name=${encodeURIComponent(searchTerm)}`,
      ),
      apiClient.get<{ clients: Client[] }>(
        `/clients?action=search&property_address=${encodeURIComponent(searchTerm)}`,
      ),
      apiClient.get<{ clients: Client[] }>(
        `/clients?action=search&property_state=${encodeURIComponent(searchTerm)}`,
      ),
    ]);

    // Curry the deduplication reducer
    const deduplicateClients = (
      acc: Record<string, Client>,
      client: Client,
    ) => ({
      ...acc,
      [client.client_id]: client,
    });

    // Functional pipeline: flatMap -> reduce -> Object.values
    const uniqueClientsDict = [nameResults, addressResults, stateResults]
      .flatMap((result) => result.clients)
      .reduce(deduplicateClients, {} as Record<string, Client>);

    return Object.values(uniqueClientsDict);
  }),
);

// Keep the old name for backward compatibility in UI components
export const filteredPropertiesFamily = atomFamily((searchTerm: string) =>
  atom(async (get) => {
    const clients = await get(filteredClientsFamily(searchTerm));
    return clients.map(clientToProperty);
  }),
);

// Derived atom that creates a property address lookup map
export const propertyAddressMapAtom = atom(async (get) => {
  const allClients = await get(allClientsAtom);
  return (allClients || []).reduce((map: Record<string, string>, client) => {
    map[client.client_id] = client.property_address;
    return map;
  }, {});
});
